import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Target, Clock, Award, TrendingUp, Shield } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useClientIntakeModal } from '../components/ModalProvider';
import SEO from '../components/SEO';

const Employers = () => {
  const { openClientIntake } = useClientIntakeModal();
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="For Employers"
        description="Partner with Omnia Paratus Talent Solutions for your hiring needs. We provide direct hire recruiting services for accounting, healthcare, logistics, and HR & people compliance positions. Find the right talent for your organization."
        keywords="hire talent, employer staffing solutions, direct hire services, recruitment partner, talent acquisition, professional staffing, find employees, hiring solutions Massachusetts"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#D4AF37] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#D4AF37] rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Find Your Next Top Performer
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-10">
            Partner with us to access exceptional talent that drives your business forward
          </p>
          <Button
            size="lg"
            className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#D4AF37]/90 text-lg px-8 py-6 shadow-xl"
            onClick={openClientIntake}
            data-testid="employers-hero-cta"
          >
            <span className="flex items-center gap-2">
              Request Talent <ArrowRight className="h-5 w-5" />
            </span>
          </Button>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44]">
              Why Partner With Us?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Clock,
                title: 'Save Time',
                description: 'We handle sourcing, screening, and initial vetting so you can focus on interviewing top candidates.'
              },
              {
                icon: Target,
                title: 'Better Quality',
                description: 'Our rigorous screening process ensures you meet only candidates who fit your requirements and culture.'
              },
              {
                icon: TrendingUp,
                title: 'Reduce Risk',
                description: '90-day placement guarantee provides peace of mind and demonstrates our confidence in our work.'
              },
              {
                icon: Users,
                title: 'Access Hidden Talent',
                description: 'Reach passive candidates not actively job searching but open to the right opportunity.'
              },
              {
                icon: Award,
                title: 'Industry Expertise',
                description: 'Specialized knowledge in your sector means we understand technical requirements and cultural fit.'
              },
              {
                icon: Shield,
                title: 'Confidentiality',
                description: 'Discreet handling of sensitive searches and protection of your proprietary information.'
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mb-6 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c] rounded-2xl flex items-center justify-center">
                      <IconComponent className="h-8 w-8 text-[#D4AF37]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0A1F44] mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="hiring-process" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              Our Hiring Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A streamlined, transparent approach that delivers results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We meet to understand your business, culture, role requirements, and ideal candidate profile.'
              },
              {
                step: '02',
                title: 'Sourcing',
                description: 'Leveraging our networks and recruiting tools, we identify and engage qualified candidates.'
              },
              {
                step: '03',
                title: 'Screening',
                description: 'Rigorous evaluation of skills, experience, and cultural fit before any candidate presentation.'
              },
              {
                step: '04',
                title: 'Placement',
                description: 'Interview coordination and support to ensure a smooth transition and successful hire.'
              }
            ].map((process, index) => (
              <div key={index} className="relative">
                <Card className="border-2 border-[#D4AF37] shadow-lg h-full">
                  <CardContent className="p-8">
                    <div className="text-5xl font-bold text-[#D4AF37] mb-4 opacity-20">{process.step}</div>
                    <h3 className="text-2xl font-bold text-[#0A1F44] mb-4">{process.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{process.description}</p>
                  </CardContent>
                </Card>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-8 w-8 text-[#D4AF37]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Look For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-6 whitespace-nowrap">
                We Screen For What Matters
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Our evaluation process goes beyond resumes to assess the complete candidate picture, 
                ensuring long-term success and cultural alignment.
              </p>

              <div className="space-y-6">
                {[
                  { label: 'Technical Skills & Experience', value: 'Required qualifications and expertise' },
                  { label: 'Cultural Fit', value: 'Values alignment and team compatibility' },
                  { label: 'Career Motivation', value: 'Goals, aspirations, and growth mindset' },
                  { label: 'Communication Style', value: 'Professional presence and interpersonal skills' },
                  { label: 'References & Background', value: 'Verification and validation' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-[#0A1F44] mb-1">{item.label}</div>
                      <div className="text-gray-600 text-sm">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {[
                { number: '95%', label: 'Interview Rate' },
                { number: '85%', label: 'Offer Rate' },
                { number: '92%', label: '1-Year Retention' },
                { number: '3-5', label: 'Candidates per Search' }
              ].map((stat, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl font-bold text-[#D4AF37] mb-2">{stat.number}</div>
                    <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Investment Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-[#D4AF37] shadow-2xl">
            <CardContent className="p-12">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-[#0A1F44] mb-4">Transparent Pricing</h2>
                <p className="text-lg text-gray-600">Simple, success-based fees with no hidden costs</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#D4AF37] mb-2">0%</div>
                  <div className="text-gray-600">Upfront Fee</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#D4AF37] mb-2">15-25%</div>
                  <div className="text-gray-600">First Year Salary</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#D4AF37] mb-2">90 Days</div>
                  <div className="text-gray-600">Guarantee Period</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-700 text-center">
                  Our contingent model means you only pay when we successfully place a candidate.
                </p>
                <p className="text-gray-700 text-center mt-1">
                  Custom pricing available for high-volume needs and retained searches.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Find Your Next Great Hire
          </h2>
          <p className="text-xl text-gray-200 mb-10">
            Start your search today with a no-obligation consultation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#D4AF37]/90 text-lg px-8 py-6 shadow-xl"
              onClick={openClientIntake}
              data-testid="employers-cta-bottom"
            >
              <span className="flex items-center gap-2">
                Request Talent <ArrowRight className="h-5 w-5" />
              </span>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-[#0A1F44] text-lg px-8 py-6"
            >
              <Link to="/services">View Services</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Employers;
